import { Modal, Form, Select, Input, message } from 'antd';
import AxiosOICStat from "AxiosOICStat";
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

const { Option } = Select;

const WdiIndicatorMappingModal = ({ visible, onCancel, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [wdiItems, setWdiItems] = useState([]);
  const [wdiSearch, setWdiSearch] = useState('');
  const searchTimer = useRef(null);
  const indicatorsState = useSelector((state) => state.indicators);

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => (a.ind_name_eng || '').localeCompare(b.ind_name_eng || ''))
    : [];

  useEffect(() => {
    if (visible) {
      fetchMappings();
      fetchWdiItems('');
      setWdiSearch('');
    }
  }, [visible]);

  const fetchMappings = async () => {
    try {
      const res = await AxiosOICStat.get('/external_api/wdi/indicator-mappings');
      setMappings(res.data?.mappings || res.data || []);
    } catch (e) {
      // ignore
    }
  };

  const fetchWdiItems = async (search = '') => {
    try {
      const q = [];
      if (typeof search === 'string' && search.trim()) q.push(`search=${encodeURIComponent(search.trim())}`);
      q.push('limit=100');
      const url = `/external_api/wdi/get-all-wdi-data?${q.join('&')}`;
      const res = await AxiosOICStat.get(url);
      const items = res.data || res.data?.items || [];
      setWdiItems(Array.isArray(items) ? items : []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchWdiItems(wdiSearch || '');
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [wdiSearch]);

  useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        oic_ind_code: record.oic_ind_code ? String(record.oic_ind_code) : undefined,
        oic_ind_name: record.oic_ind_name,
        wdi_series_code: record.wdi_series_code,
        wdi_topic: record.wdi_topic,
        wdi_indicator_name: record.wdi_indicator_name,
        multiplier: record.multiplier || 1,
      });
      if (record.wdi_series_code) {
        const exists = wdiItems.find(i => String(i.wdi_series_code) === String(record.wdi_series_code));
        if (!exists) {
          setWdiItems(prev => [{ wdi_series_code: record.wdi_series_code, wdi_indicator_name: record.wdi_indicator_name, wdi_topic: record.wdi_topic }, ...prev]);
        }
      }
    }
    if (!visible && !record) form.resetFields();
  }, [visible, record, form, wdiItems]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (record && record.id) {
        await AxiosOICStat.put(`/external_api/wdi/indicator-mappings/${record.id}`, values);
        message.success('Indicator mapping updated');
      } else {
        await AxiosOICStat.post('/external_api/wdi/indicator-mappings', values);
        message.success('Indicator mapping created');
      }
      onSuccess();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save mapping';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // simple uniqueness validators
  const validateUnique = (field, val) => {
    if (!val) return true;
    return !mappings.some(m => String(m[field] || '') === String(val) && (!record || String(m.id) !== String(record.id)));
  };

  return (
    <Modal
      title={record ? 'Edit WDI Indicator Mapping' : 'Add WDI Indicator Mapping'}
      visible={visible}
      onOk={handleSubmit}
      onCancel={() => { form.resetFields(); onCancel(); }}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="oic_ind_code"
          label="OIC Indicator"
          rules={[{ required: true, message: 'Please select an OIC indicator' }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select an indicator"
            onChange={(val) => {
              const sel = availableIndicators.find(i => String(i.ind_code) === String(val));
              console.log('Selected OIC indicator', sel.ind_name_eng);
              form.setFieldsValue({ oic_ind_name: sel.ind_name_eng });
              console.log('Form values after OIC change', form.getFieldsValue());
            }}
            optionFilterProp="children"
          >
            {availableIndicators.map(ind => (
              <Option key={String(ind.ind_code)} value={String(ind.ind_code)}>{ind.ind_code} - {ind.ind_name_eng}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="oic_ind_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item
          name="wdi_series_code"
          label="WDI Indicator"
          rules={[{ required: true, message: 'Please select a WDI indicator' }, { validator: (_, val) => {
            if (!val) return Promise.resolve();
            if (!validateUnique('wdi_series_code', val)) return Promise.reject(new Error('WDI series code already mapped'));
            return Promise.resolve();
          } }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select a WDI indicator"
            optionFilterProp="children"
            filterOption={false}
            onSearch={(val) => setWdiSearch(val)}
            onChange={(val) => {
              const sel = wdiItems.find(i => String(i.wdi_series_code || i.series_code) === String(val));
              if (sel) {
                form.setFieldsValue({
                  wdi_series_code: sel.wdi_series_code || sel.series_code,
                  wdi_topic: sel.wdi_topic || sel.topics || null,
                  wdi_indicator_name: sel.wdi_indicator_name || sel.name || null,
                });
              }
            }}
          >
            {wdiItems.map(it => (
              <Option key={it.wdi_series_code || it.series_code} value={it.wdi_series_code || it.series_code}>
                {(it.wdi_series_code || it.series_code) + ' - ' + (it.wdi_indicator_name || it.name)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="wdi_topic" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item name="wdi_indicator_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item name="multiplier" label="Multiplier" initialValue={1}>
          <Select style={{ width: 300 }}>
            <Option value={0.000000001}>Multiply ×0.000000001 (1e-9)</Option>
            <Option value={0.000001}>Multiply ×0.000001 (1e-6)</Option>
            <Option value={0.00001}>Multiply ×0.00001 (1e-5)</Option>
            <Option value={0.0001}>Multiply ×0.0001 (1e-4)</Option>
            <Option value={0.001}>Multiply ×0.001 (1e-3)</Option>
            <Option value={0.01}>Multiply ×0.01 (1e-2)</Option>
            <Option value={0.1}>Multiply ×0.1 (1e-1)</Option>
            <Option value={1}>No multiply</Option>
            <Option value={10}>Multiply ×10</Option>
            <Option value={100}>Multiply ×100</Option>
            <Option value={1000}>Multiply ×1,000</Option>
            <Option value={10000}>Multiply ×10,000</Option>
            <Option value={100000}>Multiply ×100,000</Option>
            <Option value={1000000}>Multiply ×1,000,000 (Million)</Option>
            <Option value={10000000}>Multiply ×10,000,000</Option>
            <Option value={100000000}>Multiply ×100,000,000</Option>
            <Option value={1000000000}>Multiply ×1,000,000,000 (Billion)</Option>
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WdiIndicatorMappingModal;
